import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Login from '../pages/Login';
import OriginationDashboard from '../pages/OriginationDashboard';
import Origination from '../pages/Origination';
import Clients from '../pages/Clients';
import ClientDetail from '../pages/ClientDetail';
import { useFavicon } from '../hooks/useFavicon';
import Layout from '../organisms/Layout';
import RiskAnalysisDashboard from '../pages/RiskAnalysisDashboard';
import RiskAnalysis from '../pages/RiskAnalysis';
import CommitteeDashboard from '../pages/CommitteeDashboard';
import Committee from '../pages/Committee';
import ClientApplication from '../pages/ClientApplication';
import { ROLES_CAP } from '../utils/Constants';
import { getRolesFromToken } from '../utils/Utils';
import InstrumentationDashboard from '../pages/InstrumentationDashboard';
import InstrumentationSteps from '../pages/InstrumentationSteps';
import { routes } from './routePaths';
import Dashboard from '../pages/Dashboard';
import Instrumentation from '../pages/Instrumentation';
import Users from '../pages/Users';
import NewUser from '../organisms/User/NewUser';
import Confirmation from '../pages/Confirmation';
import FinishedFoldersDashboard from '../pages/FinishedFoldersDashboard';
import GuaranteesManagement from '../pages/GuaranteesManagement';
import GuaranteesDetail from '../pages/GuaranteesDetail';
import ProductLineDetail from '../pages/ProductLineDetail';
import MonetizationDetail from '../pages/MonetizationDetail';
import MonetizationNew from '../pages/MonetizationNew';
import MonetizationEdit from '../pages/MonetizationEdit';
import Monetization from '../pages/Monetization';
import Overdue from '../pages/Overdue';
import Annexes from '../pages/Annexes';
import AnnexDetail from '../pages/AnnexDetail';
import Settings from '../pages/Settings';
import RateTypes from '../pages/RateTypes';
import Sgr from '../pages/Sgr';
import FundsAndProtectors from '../pages/FundsAndProtectors';
import ProtectorDetail from '../pages/ProtectorDetail';
import ProtectorNew from '../pages/ProtectorNew';
import ProtectorContributionNew from '../pages/ProtectorContributionNew';
import Partners from '../pages/Partners';
import Funds from '../pages/Funds';
import AssetsToIntegrate from '../pages/AssetsToIntegrate';
import Quotations from '../pages/Quotations';
import QuotationsDetail from '../pages/QuotationsDetail';

const origination = `${routes.clients}`;
const originationDetail = `${routes.clients}/:cuit/applications/:id`;

const risksAnalysisDashboard = `${routes.risksAnalysis}`;
const risksAnalysisDetail = `${routes.risksAnalysis}/:cuit/applications/:id`;

const committeeDashboard = `${routes.committee}`;
const committeeDetail = `${routes.committee}/:cuit/applications/:id`;


const clientApplicationDetail = `${routes.clients}/:cuit/application/:id`;

const instrumentationDashboard = `${routes.instrumentation}`;
const instrumentationDetail = `${routes.instrumentation}/:cuit/applications/:id`;
const instrumentationSteps = `${routes.instrumentation}/:cuit/applications/:id/steps`;

const dashboard = `${routes.dashboardComercial}`;
const users = `${routes.users}`;
const newUser = `${routes.users}/nuevoUsuario`;
const finishedFoldersDashboard = `${routes.finishedFolders}`;
const guarantees = `${routes.guarantees}`;
const guaranteeDetail = `${routes.guaranteeDetail}`;
const productLineDetail = `${routes.productLineDetail}`;
const loanDetail = `${routes.loanDetail}`;
const checkDetail = `${routes.checkDetail}`;
const promissoryNoteDetail = `${routes.promissoryNoteDetail}`;
const commercialEndorsementDetail = `${routes.commercialEndorsementDetail}`;
const monetization = `${routes.monetization}`;
const overdue = `${routes.overdue}`;
const fundsAndProtectors = `${routes.fundsAndProtectors}`;
const protectorDetail = `${routes.protectorDetail}`;

const config = JSON.parse(localStorage.getItem('config') ?? '{}');
const isClientEnabled = config?.isClientEnabled ?? false;
const isGuaranteesEnabled = config?.isGuaranteesEnabled ?? false;
const isQuotationsEnabled = config?.isQuotationsEnabled ?? false;
const isInstrumentationEnabled = config?.isInstrumentationEnabled ?? true;

const withLayout = (Component: React.ComponentType) => (
  <Layout>
    <Component />
  </Layout>
);


const baseOriginationRoutes = [
  { path: routes.dashboard, element: withLayout(OriginationDashboard) },
  { path: origination, element: withLayout(Origination) },
  { path: originationDetail, element: withLayout(Origination) },
];

const riskRoutes = [
  { path: risksAnalysisDashboard, element: withLayout(RiskAnalysisDashboard) },
  { path: risksAnalysisDetail, element: withLayout(RiskAnalysis) },
];

const committeeRoutes = [
  { path: committeeDashboard, element: withLayout(CommitteeDashboard) },
  { path: committeeDetail, element: withLayout(Committee) },
];

const instrumentationRoutes = [
  {
    path: instrumentationDashboard,
    element: withLayout(InstrumentationDashboard),
  },
  { path: instrumentationDetail, element: withLayout(Instrumentation) },
  { path: instrumentationSteps, element: withLayout(InstrumentationSteps) },
];

const finishedFoldersRoutes = [
  { path: finishedFoldersDashboard, element: withLayout(FinishedFoldersDashboard) },
];

const dashboardRoutes = [
  {
    path: dashboard,
    element: withLayout(Dashboard),
  },
];

const usersRoutes = [
  {
    path: users,
    element: withLayout(Users),
  },
  {
    path: newUser,
    element: withLayout(NewUser),
  },
];

const clientRoutes = [
  { path: routes.clientsList, element: withLayout(Clients) },
  { path: routes.clientDetail, element: withLayout(ClientDetail) },
  { path: clientApplicationDetail, element: withLayout(ClientApplication) },
];

const settingsRoutes = [
  {
    path: routes.settings,
    element: withLayout(Settings),
  },
  {
    path: routes.quotations,
    element: withLayout(Quotations),
  },
  {
    path: routes.quotationsDetail,
    element: withLayout(QuotationsDetail),
  },
];

const guaranteesRoutes = [
  {
    path: guarantees,
    element: withLayout(GuaranteesManagement),
  },
  {
    path: routes.annexes,
    element: withLayout(Annexes),
  },
  {
    path: routes.newAnnex,
    element: withLayout(AnnexDetail),
  },
  {
    path: routes.annexDetail,
    element: withLayout(AnnexDetail),
  },
  {
    path: guaranteeDetail,
    element: withLayout(GuaranteesDetail),
  },
  {
    path: productLineDetail,
    element: withLayout(ProductLineDetail),
  },
  {
    path: loanDetail,
    element: withLayout(MonetizationDetail),
  },
  {
    path: checkDetail,
    element: withLayout(MonetizationDetail),
  },
  {
    path: promissoryNoteDetail,
    element: withLayout(MonetizationDetail),
  },
  {
    path: commercialEndorsementDetail,
    element: withLayout(MonetizationDetail),
  },
  {
    path: monetization,
    element: withLayout(Monetization),
  },
  {
    path: routes.newMonetization,
    element: withLayout(MonetizationNew),
  },
  {
    path: routes.editMonetization,
    element: withLayout(MonetizationEdit),
  },
  {
    path: overdue,
    element: withLayout(Overdue),
  },
  {
    path: fundsAndProtectors,
    element: withLayout(FundsAndProtectors),
  },
  {
    path: protectorDetail,
    element: withLayout(ProtectorDetail),
  },
  {
    path: routes.protectorNew,
    element: withLayout(ProtectorNew),
  },
  {
    path: routes.protectorContributionNew,
    element: withLayout(ProtectorContributionNew),
  },
    {
    path: routes.rateTypes,
    element: withLayout(RateTypes),
  },
  {
    path: routes.sgr,
    element: withLayout(Sgr),
  },
  {
    path: routes.partners,
    element: withLayout(Partners),
  },
  {
    path: routes.funds,
    element: withLayout(Funds),
  },
  {
    path: routes.assetsToIntegrate,
    element: withLayout(AssetsToIntegrate),
  },
];

const routesForProducer = [...baseOriginationRoutes];
let routesForNonProducer = [
  ...baseOriginationRoutes,
  ...riskRoutes,
  ...committeeRoutes,
  ...dashboardRoutes,
  ...usersRoutes,
  ...finishedFoldersRoutes,
];

if (isInstrumentationEnabled) {
  routesForNonProducer = [...routesForNonProducer, ...instrumentationRoutes];
}

if (isClientEnabled) {
  routesForNonProducer = [...routesForNonProducer, ...clientRoutes];
}



const AppRoutes = () => {
  const roles: string[] = getRolesFromToken();
  
  // Cargar favicon desde CloudFront si está disponible
  useFavicon();

  let allowedRoutes: { path: string; element: JSX.Element }[] = [];

  if (roles.includes(ROLES_CAP.producer)) {
    allowedRoutes = [...allowedRoutes, ...routesForProducer];
  }
  if (roles.includes(ROLES_CAP.commercial)) {
    allowedRoutes = [...allowedRoutes, ...routesForNonProducer];
  }
  if (roles.includes(ROLES_CAP.comite)) {
    allowedRoutes = [...allowedRoutes, ...routesForNonProducer];
  }
  if (roles.includes(ROLES_CAP.instrumentator)) {
    allowedRoutes = [...allowedRoutes, ...routesForNonProducer];
  }
  if (isClientEnabled) {
    allowedRoutes = [...allowedRoutes, ...clientRoutes];
  }
  if (isGuaranteesEnabled) {
    allowedRoutes = [...allowedRoutes,  ...guaranteesRoutes];
  }
  if (isQuotationsEnabled || isGuaranteesEnabled) {
    allowedRoutes = [...allowedRoutes, ...settingsRoutes];
  }


  const uniqueRoutes = Array.from(
    new Map(allowedRoutes.map((r) => [r.path, r])).values()
  );

  return (
    <Router>
      <Routes>
        {uniqueRoutes.map((route, index) => (
          <Route key={index} path={route.path} element={route.element} />
        ))}
        <Route path='/confirmacion/:id' element={<Confirmation />} />
        <Route path='*' element={<Login />} />
      </Routes>
    </Router>
  );
};

export default AppRoutes;
